import React from 'react';
import { useSelector } from 'react-redux';

import { ROLLOUT_CONTINUE_WATCHING } from 'constants/featureFlag';
import { PERMISSION_GRANTED } from 'constants/notifications';
import {
  REQUEST_PUSH_PERMISSIONS,
  SHOW_DATA_RETENTION_WARNING,
  SHOW_MEMBER_VIDEO_THRESHOLD_BANNER,
  SHOW_MOBILE_BANNER_PROMPT_ON_SHARE_PAGE,
} from 'constants/triggers';

import { useShowContinueWatchingBanner } from 'hooks/banners';
import { useShouldShowTrigger, useUrlTriggerReset } from 'hooks/triggers';
import {
  useCheckIfSdkSharedUser,
  useGetMemberVideoLimits,
} from 'hooks/workspace';
import { selectUserIsOwner } from 'selectors/shareVideo';
import { selectUserIsLogged } from 'selectors/user';

import MemberVideoThresholdBanner from './MemberVideoThresholdBanner';

import MobileDownloadBanner from 'components/account-settings/mobile-download-banner';
import { SharePageDataRetentionBanner } from 'components/share-video/data-retention-banner';
import WebPermissionsBanner from 'components/web-permissions-banners/share-page';

const Banners = () => {
  const isLoggedIn = useSelector(selectUserIsLogged);
  const shouldRequestPushPermissions = useShouldShowTrigger(
    REQUEST_PUSH_PERMISSIONS
  );
  const hasRecordedFirstVideo = useSelector(
    state => state.checklist.first_video_recording
  );

  const isVideoOwner = useSelector(selectUserIsOwner);

  const shouldShowMobileBanner = useShouldShowTrigger(
    SHOW_MOBILE_BANNER_PROMPT_ON_SHARE_PAGE
  );

  const shouldShowDataRetentionBanner =
    useShouldShowTrigger(SHOW_DATA_RETENTION_WARNING) && isVideoOwner;

  const browserSupport = 'Notification' in window;

  const memberVideoLimit = useGetMemberVideoLimits();
  const showMemberVideoThresholdBanner = useShouldShowTrigger(
    SHOW_MEMBER_VIDEO_THRESHOLD_BANNER
  );
  const isSdkSharedUser = useCheckIfSdkSharedUser();

  useUrlTriggerReset({
    paramName: 'permissionsOverride',
    trigger: REQUEST_PUSH_PERMISSIONS,
  });

  const shouldLoadContinueWatchingBanner = useShowContinueWatchingBanner();
  const isLoadingBannerFlag = useSelector(state => {
    const flagData = state.feature_flags[ROLLOUT_CONTINUE_WATCHING];

    if (!flagData) {
      return false;
    }

    return flagData.fetching && !flagData.fetched;
  });

  const hideBanners = shouldLoadContinueWatchingBanner || isLoadingBannerFlag;

  // To avoid banner fatigue we do not show these banners while some other
  // banners are being displayed
  if (hideBanners) {
    return null;
  }

  if (showMemberVideoThresholdBanner && memberVideoLimit.nearLimit) {
    return <MemberVideoThresholdBanner memberVideoLimit={memberVideoLimit} />;
  }

  const showWebPermissions =
    isLoggedIn &&
    isVideoOwner &&
    shouldRequestPushPermissions &&
    hasRecordedFirstVideo &&
    !isSdkSharedUser &&
    browserSupport &&
    Notification.permission !== PERMISSION_GRANTED;

  if (showWebPermissions) {
    return <WebPermissionsBanner />;
  }

  const showMobileBanner =
    isLoggedIn &&
    shouldShowMobileBanner &&
    hasRecordedFirstVideo &&
    browserSupport &&
    Notification.permission === PERMISSION_GRANTED;

  if (showMobileBanner) {
    return <MobileDownloadBanner onSharePage />;
  }

  if (shouldShowDataRetentionBanner) {
    return <SharePageDataRetentionBanner />;
  }

  return null;
};

export default Banners;
